import { filter, isEmpty, values } from 'ramda';
import type { Dictionary } from 'ramda/tools';
import { Weddings } from '@bridebook/models';
import type { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import type { ISupplier as IWeddingSupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import { PremiumTiers } from '@bridebook/toolbox/src';
import type { IEnrichedSupplierOutgoing } from '@bridebook/toolbox/src/enrich-suppliers/enrich-suppliers';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { isIUISupplier, IUISupplier, Slug } from '@bridebook/toolbox/src/types';
import type { ICustomTarget } from '@bridebook/ui/src/types';
import { prettySupplierCategory } from '@bridebook/web/app-shared/lib/utils';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { updateUserBudget } from '@bridebook/web/lib/budget/actions';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { getCountryCode } from '@bridebook/web/lib/i18n/selectors';
import { hapticFeedbackLight } from '@bridebook/web/lib/mobile-app/utils/haptic-feedback-light';
import { getOpenedSupplierDetails } from '@bridebook/web/lib/shortlist/selectors';
import type {
  BookedMethod,
  RemovedRecommendationLocation,
  RemovedRecommendationMethod,
  ShortlistedLocation,
  ShortlistedMethod,
  TShortlistList,
  TShortlistTab,
} from '@bridebook/web/lib/shortlist/types';
import { upsertWeddingSupplier } from '@bridebook/web/lib/shortlist/utils';
import { SupplierActions } from '@bridebook/web/lib/supplier/actions-types';
import { getCurrentSupplierId, getPremium } from '@bridebook/web/lib/supplier/selectors';
import { isSupplierVenue } from '@bridebook/web/lib/supplier/utils';
import { TrackingEvent } from '@bridebook/web/lib/track-utils/tracking-event';
import { toggleShowroundPromoModal } from '@bridebook/web/lib/ui/actions';
import {
  Action,
  IDeps,
  IElasticSupplier,
  RecommendationsEngineVersion,
} from '@bridebook/web/lib/types';
import { noopAction } from '@bridebook/web/lib/utils';
import { closeBookingConfirmationModal } from '@bridebook/web/lib/venue-confirmation/actions';
import { saveVenueLocation } from '@bridebook/web/lib/weddings/actions';
import { ShortlistActionTypes } from './action-types';
import { blockAccount, verifySpamCheck } from '@bridebook/web/lib/enquiries/utils/spam-check';

const getBookedSnackbarMessage = () => getI18n().t('common:shortlist.snackbar.booked');

export const shortlistedSupplierAnalytics = (payload: {
  supplier: IUISupplier;
  method: ShortlistedMethod;
  shortlistedLocation?: ShortlistedLocation;
  shortlistedSection?: string;
  linkedSupplierName?: string;
  linkedSupplierId?: string;
  recommendationEngineUsed?: boolean;
  recommendationEngineVersion?: RecommendationsEngineVersion;
  supplierPhotoUrls?: string[];
}) => ({
  type: 'SHORTLISTED_SUPPLIER_ANALYTICS',
  payload,
});

export const saveToShortlist =
  ({
    supplier,
    method,
    shortlistedLocation,
    shortlistedSection,
    showSnackbar = true,
    linkedSupplierId,
    linkedSupplierName,
    disableHapticFeedback,
    recommendationEngineUsed,
    recommendationEngineVersion,
    skipRecommendations,
    triggerShowroundPromo,
    supplierPhotoUrls,
  }: {
    supplier: IUISupplier & { booked?: boolean };
    method: ShortlistedMethod;
    shortlistedLocation?: ShortlistedLocation;
    showSnackbar?: boolean;
    linkedSupplierName?: string;
    linkedSupplierId?: string;
    disableHapticFeedback?: boolean;
    shortlistedSection?: string;
    recommendationEngineUsed?: boolean;
    recommendationEngineVersion?: RecommendationsEngineVersion;
    // Skip showing recommended suppliers modal after booking a venue
    skipRecommendations?: boolean;
    triggerShowroundPromo?: boolean;
    supplierPhotoUrls?: string[];
  }) =>
  ({ getState, dispatch, cordovaTracker }: IDeps) => {
    const state = getState();
    const {
      weddings: {
        profile: { id: weddingId },
      },
      app: {
        device: { isCordova },
        pathname,
      },
    } = state;

    let isVenue;
    const isBooking = Boolean(supplier.booked);

    // Validate for spam
    if (isBooking && !verifySpamCheck(state)) {
      blockAccount(state);
      return {
        type: ShortlistActionTypes.SAVE_TO_SHORTLIST_ERROR,
      };
    }

    /** haptic feedback only when adding/removing from shortlist */
    if (!disableHapticFeedback && !isBooking && isCordova) {
      hapticFeedbackLight();
    }
    if (supplier.id) {
      // Capture shortlist status before the upsert to avoid duplicate analytics
      const alreadyShortlisted = Boolean(state.shortlist.list[supplier.id]);

      upsertWeddingSupplier({
        supplier,
        weddingId,
        overwrite: {
          shortlisted: true,
          ignored: false,
          booked: Boolean(supplier.booked),
          enquired: Boolean(supplier.enquired),
        },
      })
        .then((wedding) => {
          // @ts-expect-error
          wedding.increment('suppliers.count');
          if (alreadyShortlisted) return;
          cordovaTracker.track(TrackingEvent.ShortlistedSupplier, supplier);
          dispatch(
            shortlistedSupplierAnalytics({
              supplier,
              method,
              shortlistedLocation,
              shortlistedSection,
              linkedSupplierName,
              linkedSupplierId,
              recommendationEngineUsed,
              recommendationEngineVersion,
              supplierPhotoUrls,
            }),
          );
          if (supplier.type === 'venue' && triggerShowroundPromo) {
            dispatch(
              toggleShowroundPromoModal({
                isVisible: true,
                supplierId: supplier.id,
                actionLocationAnalytics: shortlistedLocation ?? null,
              }),
            );
          }
        })
        // eslint-disable-next-line no-console
        .catch((error) => console.error('SHORT:ERROR', error));
    }

    if (isBooking) {
      dispatch(addedToBookedSuppliers(supplier, skipRecommendations));
      const { town, county, country, type } = supplier;
      isVenue = type === 'venue';

      if (isVenue) {
        // CountryCode is only needed until we translate supplier-couple portal, as we only collaborate with UK venues for now
        const countryCode = supplier.countryCode ?? CountryCodes.GB;

        if (countryCode) {
          dispatch(
            // LIVE-18673 Keeping wedding address the same as booked supplier address
            saveVenueLocation(
              {
                ...(town && { name: town, city: town }),
                ...(county && country && { adminArea: [county, country] }),
                country: countryCode,
              },
              supplier.customSupplier,
            ),
          );
        }
      }
    }
    // Don't show snackbar for venue booking due to discover venue modal
    const venueBooking = isBooking && isVenue;
    const showSnackbarShortlistLink =
      shortlistedLocation !== 'shortlist' && !pathname.startsWith('/shortlist');

    if (showSnackbar && !venueBooking) {
      dispatch(
        toggleSnackbar(
          'success',
          isBooking
            ? getBookedSnackbarMessage()
            : getI18n().t('common:shortlist.snackbar.shortlisted'),
          undefined,
          showSnackbarShortlistLink
            ? {
                action: 'goToFavourites',
                actionData: {
                  supplierType: supplier.type,
                  supplierId: supplier.id,
                },
                analyticsData: {
                  category: 'Shortlist',
                  cta: 'View favourites page from favourites success banner',
                  clickedSection: 'supplierProfile',
                  clickedLocation: 'favouritesSuccessBanner',
                  supplierId: supplier.id,
                },
                icon: '🎉',
              }
            : { icon: '🎉' },
        ),
      );
    }

    return saveToShortlistEnriched(supplier);
  };

/** TS hell, we need another return type to satisfy types in shortlistChildAddedEpic */
export const saveToShortlistEnriched = (payload: IUISupplier & { booked?: boolean }) => ({
  type: ShortlistActionTypes.SAVE_TO_SHORTLIST,
  payload,
});

export const fetchVenueRecommendedSuppliersSuccess =
  (recommendedSuppliers: IElasticSupplier[]) => () => ({
    type: ShortlistActionTypes.FETCH_VENUE_RECOMMENDED_SUPPLIERS_SUCCESS,
    payload: recommendedSuppliers,
  });

export const removeFromShortlist =
  (
    supplier: IUISupplier | IWeddingSupplier,
    method: string,
    shortlistedLocation?: ShortlistedLocation,
    recommendationEngineUsed?: boolean,
    recommendationEngineVersion?: RecommendationsEngineVersion,
  ) =>
  ({ dispatch, getState, lostLeadService }: IDeps): Action => {
    const {
      weddings: {
        profile: { id: weddingId },
      },
      app: {
        device: { isCordova },
      },
    } = getState();

    /** Haptic feedback on mobile */
    if (isCordova) {
      hapticFeedbackLight();
    }

    if (supplier.id) {
      const getSupplierTier = () => {
        if (isIUISupplier(supplier)) {
          return supplier.tier;
        }

        /**
         * removeFromShortlist can be triggered from multiple places. Sometimes the supplier data
         * comes from elastic (IUISupplier) and sometimes from firebase (IWeddingSupplier aka
         * ISupplier). Because ISupplier does not hold the tier information, we need to try to get
         * it from the store. But because there's currently no guarantee that the stored supplier is
         * the same supplier we're removing from shortlist,, we need to first make sure we're
         * dealing with proper supplier (by comparing the supplier id with the current supplier id).
         *
         * The above is a temporary solution until we refactor the supplier data structure and it's
         * mainly to cover the unfavouriting from supplier profile page.
         */
        const state = getState();
        const currentSupplierPremium = getPremium(state);
        const currentSupplierId = getCurrentSupplierId(state);
        if (supplier.id === currentSupplierId) {
          return currentSupplierPremium?.tier;
        }

        return PremiumTiers.Unverified;
      };
      const wedding = Weddings._.getById(weddingId);

      upsertWeddingSupplier({
        supplier,
        weddingId,
        overwrite: { shortlisted: false, booked: false },
      }).then(() => {
        const supplierTier = getSupplierTier();
        const lostLeadModalAvailable =
          supplier &&
          lostLeadService.checkAvailability(
            {
              countryCode: isIUISupplier(supplier)
                ? supplier.countryCode || supplier.l10n?.country
                : supplier.address?.country,
              supplierId: supplier.id,
              supplierTier,
              supplierType: supplier.type,
            },
            supplier,
          );

        // @ts-expect-error
        wedding.decrement('suppliers.count');
        // Show lost lead modal
        if (lostLeadModalAvailable) {
          lostLeadService.openLostLeadModal(supplier.id, 'Unfavouriting');
        }
        dispatch({
          type: 'REMOVED_SUPPLIER_FROM_SHORTLIST_ANALYTICS',
          payload: {
            method,
            supplier,
            shortlistedLocation,
            recommendationEngineUsed,
            recommendationEngineVersion,
          },
        });
      });
    }

    dispatch(toggleSnackbar('info', getI18n().t('common:shortlist.snackbar.removed')));

    return {
      type: ShortlistActionTypes.REMOVE_FROM_SHORTLIST,
      payload: { id: supplier.id },
    };
  };

export const fetchUserShortlistSuccess = (payload: { shortlisted: TShortlistList }) => ({
  type: ShortlistActionTypes.FETCH_USER_SHORTLIST_SUCCESS,
  payload,
});

export const onRemovedSuppliersFromShortlist = (payload?: IWeddingSupplier[]) => ({
  type: ShortlistActionTypes.ON_REMOVED_SUPPLIERS_FROM_SHORTLIST,
  payload,
});

export const onSuppliersShortlistedSuccess = (
  payload: (IEnrichedSupplierOutgoing | IWeddingSupplier)[],
) => ({
  type: ShortlistActionTypes.ON_SUPPLIERS_SHORTLISTED_SUCCESS,
  payload,
});

export const onIgnoredSuppliersSuccess = ({
  suppliersToAddList,
  suppliersToRemoveList,
}: {
  suppliersToAddList: IWeddingSupplier[];
  suppliersToRemoveList: IWeddingSupplier[];
}) => ({
  type: ShortlistActionTypes.ON_IGNORED_SUPPLIERS_SUCCESS,
  payload: {
    suppliersToAddList,
    suppliersToRemoveList,
  },
});

// skipRecommendations: optional flag to prevent recommended suppliers modal from showing
export const addedToBookedSuppliers = (item: IUISupplier, skipRecommendations?: boolean) => ({
  type: ShortlistActionTypes.ADDED_TO_BOOKED_SUPPLIERS,
  payload: { item, skipRecommendations },
});

export const shortlistBookSupplier =
  (item: IUISupplier, method: string) =>
  ({ dispatch }: IDeps) => {
    dispatch(addedToBookedSuppliers(item));

    dispatch(toggleSnackbar('success', getBookedSnackbarMessage()));
    return {
      type: ShortlistActionTypes.BOOK_SUPPLIER,
      payload: { item, method },
    };
  };

export const showUnbookSupplierWarning =
  ({ supplier, method, section }: { supplier: IUISupplier; method: string; section?: string }) =>
  ({ getState, dispatch, warningModal }: IDeps) => {
    const state = getState();
    const countryCode = getCountryCode(state);
    const market = gazetteer.getMarketByCountry(countryCode as CountryCodes);
    const supplierCategory = prettySupplierCategory({
      slug: supplier.type,
      amount: 1,
      isLoggedIn: true,
      market,
    });
    const isVenue = isSupplierVenue(supplier);

    warningModal.toggleWarningModal({
      title: getI18n().t('shortlistModals:unbookConfirmation.title', {
        supplierCategory,
      }),
      subtitle: isVenue
        ? getI18n().t('shortlistModals:unbookConfirmation.subtitleVenue')
        : getI18n().t('shortlistModals:unbookConfirmation.subtitleDefault', {
            supplierName: supplier.name,
          }),
      type: 'confirm',
      onConfirm: () => {
        dispatch(unbookSupplier(supplier, method, section));
      },
      confirmText: getI18n().t('shortlistModals:unbookConfirmation.confirm'),
    });

    return noopAction();
  };

export const unbookSupplier = (item: IUISupplier, method: string, section?: string) => ({
  type: ShortlistActionTypes.UNBOOK_SUPPLIER,
  payload: { item, method, section },
});

export const clearCustomSupplierForm = () => ({
  type: ShortlistActionTypes.CLEAR_CUSTOM_SUPPLIER_FORM,
});

export const toggleSupplierAddModal = (
  open: boolean = false,
  triggerLocation?: string,
  addToBooked?: boolean,
  defaultCategory?: Slug,
) => ({
  type: ShortlistActionTypes.TOGGLE_SUPPLIER_ADD_MODAL,
  payload: {
    open,
    triggerLocation,
    addToBooked,
    defaultCategory,
  },
});

export const createCustomSupplierSuccess =
  (
    supplier: IUISupplier,
    method: BookedMethod,
    booked?: boolean,
    shortlistedLocation?: ShortlistedLocation,
    showSnackbar?: boolean,
  ) =>
  ({ dispatch, getState }: IDeps) => {
    const {
      shortlist: { addToBooked },
    } = getState();
    dispatch(
      saveToShortlist({
        supplier: { ...supplier, booked: booked || addToBooked },
        method: 'tile',
        shortlistedLocation,
        showSnackbar,
      }),
    );
    dispatch(clearCustomSupplierForm());
    return {
      type: ShortlistActionTypes.CREATE_CUSTOM_SUPPLIER_SUCCESS,
      payload: { supplier, method },
    };
  };

export const customSupplierFormShowNextError = () => ({
  type: ShortlistActionTypes.CUSTOM_SUPPLIER_FORM_SHOW_NEXT_ERROR,
});

export const customSupplierFormValid = () => ({
  type: ShortlistActionTypes.CUSTOM_SUPPLIER_FORM_VALID,
});

export const createCustomSupplier = (
  method: BookedMethod,
  supplier?: IUISupplier,
  booked?: boolean,
  shortlistedLocation?: ShortlistedLocation,
  showSnackbar: boolean = true,
) => ({
  type: ShortlistActionTypes.CREATE_CUSTOM_SUPPLIER,
  payload: {
    method,
    supplier,
    booked,
    shortlistedLocation,
    showSnackbar,
  },
});

export const setCustomSupplierFormField = (event: ICustomTarget) => {
  const {
    target: { name, value },
  } = event;
  return {
    type: ShortlistActionTypes.SET_CUSTOM_SUPPLIER_FORM_FIELD,
    payload: { name, value },
  };
};

export const clickedSupplierContactLink = (
  supplier: ISupplier | undefined,
  type: string,
  location: string,
): Action => ({
  type: SupplierActions.CLICKED_SUPPLIER_CONTACT_LINK_ANALYTICS,
  payload: { supplier, type, location },
});

export const toggleSupplierDetailsDrawer = (supplierId: string | null = null) => ({
  type: ShortlistActionTypes.TOGGLE_SUPPLIER_DETAILS_DRAWER,
  payload: { supplierId },
});

/**
 * Save notes inside the supplier details drawer
 * @param notes
 */
export const saveShortlistedSupplierNotes =
  ({ notes }: { notes: IWeddingSupplier['notes'] }) =>
  ({ getState, dispatch }: IDeps) => {
    const previousSupplier = getOpenedSupplierDetails(getState()) || ({} as IWeddingSupplier);
    const supplier = { ...previousSupplier, notes } as IWeddingSupplier;
    const isChanged = supplier.notes !== previousSupplier.notes;

    if (isChanged) {
      dispatch({
        type:
          previousSupplier.notes === ''
            ? 'ADDED_SUPPLIER_NOTE_ANALYTICS'
            : 'EDITED_SUPPLIER_NOTE_ANALYTICS',
        payload: { supplier },
      });
    }

    return updateShortlistedSupplier({ supplier, updatedProperty: { notes } });
  };

/**
 * Save quote inside the supplier details drawer
 * @param price
 */
export const saveShortlistedSupplierPrice =
  ({ price }: { price: IWeddingSupplier['price'] }) =>
  ({ getState, dispatch }: IDeps) => {
    const previousSupplier = getOpenedSupplierDetails(getState()) || ({} as IWeddingSupplier);
    const supplier = { ...previousSupplier, price } as IWeddingSupplier;
    const isChanged = supplier.price !== previousSupplier.price;

    if (isChanged) {
      if (supplier.booked) dispatch(updateUserBudget(supplier.type));
      dispatch({
        type: !previousSupplier.price
          ? 'ADDED_SUPPLIER_QUOTE_ANALYTICS'
          : 'EDITED_SUPPLIER_QUOTE_ANALYTICS',
        payload: {
          supplier,
        },
      });
    }

    return updateShortlistedSupplier({ supplier, updatedProperty: { price } });
  };

/**
 * Save visit date inside the supplier details drawer
 * @param visitDate
 */
export const saveShortlistedSupplierVisitDate =
  ({ visitDate }: { visitDate: IWeddingSupplier['visitDate'] }) =>
  ({ getState, dispatch }: IDeps) => {
    const previousSupplier = getOpenedSupplierDetails(getState()) || ({} as IWeddingSupplier);
    const supplier = { ...previousSupplier, visitDate } as IWeddingSupplier;
    const isChanged = supplier.visitDate !== previousSupplier.visitDate;

    if (isChanged) {
      const isAdded = !previousSupplier.visitDate || isEmpty(previousSupplier.visitDate);
      const isRemoved = !supplier.visitDate || isEmpty(supplier.visitDate);

      if (isAdded) {
        dispatch(addedSupplierVisitDateAnalytics(supplier));
      } else if (isRemoved) {
        dispatch(removedSupplierVisitDateAnalytics(supplier));
      } else {
        dispatch(editedSupplierVisitDateAnalytics(supplier));
      }
    }

    return updateShortlistedSupplier({ supplier, updatedProperty: { visitDate } });
  };

type TUpdatedProperty =
  | { visitDate: IWeddingSupplier['visitDate'] }
  | { notes: IWeddingSupplier['notes'] }
  | { price: IWeddingSupplier['price'] };

export const updateShortlistedSupplier = (payload: {
  supplier: IWeddingSupplier;
  updatedProperty: TUpdatedProperty;
}) => ({
  type: ShortlistActionTypes.UPDATE_SHORTLISTED_SUPPLIER,
  payload,
});

export const toggledTabNavigationOnShortlistAnalytics = (tab: TShortlistTab) => ({
  type: ShortlistActionTypes.TOGGLED_TAB_NAVIGATION_ON_SHORTLIST_ANALYTICS,
  payload: tab,
});

export const unbookAllVenues =
  () =>
  ({ getState }: IDeps) => {
    const { list } = getState().shortlist;
    const isBookedVenue = (item: IWeddingSupplier) => item.booked && isSupplierVenue(item);
    const bookedVenues = filter<IUISupplier & IWeddingSupplier>(
      isBookedVenue,
      list as Dictionary<IUISupplier & IWeddingSupplier>,
    );

    const venueIds = values(bookedVenues).map((venue) => venue.id);

    return {
      type: ShortlistActionTypes.UNBOOK_ALL_VENUES,
      payload: { venueIds },
    };
  };

/**
 * Toggle modal to add a new international supplier.
 * If category (slug) is passed, the form is locked to this category
 * and the category dropdown is hidden. If not, defaults to 'venue' and
 * the category dropdown is shown to allow selection.
 */
// TODO: Revisit after discussing Google Location controls unification @adgutbb
export const toggleGoogleSearchModal = ({
  show = false,
  category,
  addToBooked,
}: {
  show: boolean;
  category?: Slug | '';
  addToBooked?: boolean;
}) => ({
  type: ShortlistActionTypes.TOGGLE_GOOGLE_SEARCH_MODAL,
  payload: { show, category, addToBooked },
});

export const toggleCustomSupplierFormModal = (
  show = false,
  finished = false,
  method = 'shortlist',
  defaultCategory: Slug = 'venue',
  addToBooked?: boolean,
) => ({
  type: ShortlistActionTypes.TOGGLE_CUSTOM_SUPPLIER_FORM_MODAL,
  payload: { show, finished, method, defaultCategory, addToBooked },
});

export const markIgnored =
  (supplier: IUISupplier) =>
  ({ getState }: IDeps) => {
    const {
      weddings: {
        profile: { id },
      },
    } = getState();

    upsertWeddingSupplier({
      supplier,
      weddingId: id,
      overwrite: { shortlisted: false, ignored: true, booked: false },
    });

    return {
      type: ShortlistActionTypes.MARK_SUPPLIER_IGNORED,
      payload: { supplier },
    };
  };

export const removedFromRecommendationsAnalytics = ({
  supplier,
  location,
  method,
  cta,
  removed = true,
  recommendationEngineUsed,
  recommendationEngineVersion,
}: {
  supplier: IUISupplier;
  location?: RemovedRecommendationLocation;
  method?: RemovedRecommendationMethod;
  cta?: string;
  removed?: boolean;
  recommendationEngineUsed?: boolean;
  recommendationEngineVersion?: RecommendationsEngineVersion;
}) => ({
  type: ShortlistActionTypes.REMOVED_SUPPLIER_FROM_RECOMMENDATIONS_ANALYTICS,
  payload: {
    supplier,
    location,
    method,
    cta,
    removed,
    recommendationEngineUsed,
    recommendationEngineVersion,
  },
});

export const setShortlistedLocation = (shortlistedLocation: ShortlistedLocation) => ({
  type: ShortlistActionTypes.SET_SHORTLISTED_LOCATION,
  payload: { shortlistedLocation },
});

export const triggeredBookedSupplierPopupAnalytics = (bookedPopupLocation?: string) => ({
  type: ShortlistActionTypes.TRIGGERED_BOOKED_SUPPLIER_POP_UP_ANALYTICS,
  payload: bookedPopupLocation,
});

export const triggeredAddSupplierPopupAnalytics = () => ({
  type: ShortlistActionTypes.TRIGGERED_ADD_SUPPLIER_POP_UP_ANALYTICS,
});

export const addedSupplierVisitDateAnalytics = (supplier: IWeddingSupplier) => ({
  type: ShortlistActionTypes.ADDED_SUPPLIER_VISIT_DATE_ANALYTICS,
  payload: supplier,
});

export const editedSupplierVisitDateAnalytics = (supplier: IWeddingSupplier) => ({
  type: ShortlistActionTypes.EDITED_SUPPLIER_VISIT_DATE_ANALYTICS,
  payload: supplier,
});

export const removedSupplierVisitDateAnalytics = (supplier: IWeddingSupplier) => ({
  type: ShortlistActionTypes.REMOVED_SUPPLIER_VISIT_DATE_ANALYTICS,
  payload: supplier,
});

export const bookedSupplierAnalytics = ({
  supplier,
  method,
  section,
}: {
  supplier: IUISupplier;
  method: BookedMethod;
  section?: string;
}) => ({
  type: ShortlistActionTypes.BOOKED_SUPPLIER_ANALYTICS,
  payload: { item: supplier, method, section },
});

export const unbookSupplierAnalytics = ({
  supplier,
  method,
}: {
  supplier: ISupplier;
  method: BookedMethod;
}) => ({
  type: ShortlistActionTypes.UNBOOK_SUPPLIER_ANALYTICS,
  payload: { item: supplier, method },
});

/**
 * Toggle modal to confirm marking supplier as booked
 */
export const toggleConfirmBookingModal = (
  supplierToBook: IUISupplier | null,
  triggerLocation = '',
) => ({
  type: ShortlistActionTypes.TOGGLE_CONFIRM_BOOKING_MODAL,
  payload: { supplierToBook, triggerLocation },
});

/**
 * Confirm marking a supplier as booked
 */
export const confirmBookingSupplier =
  (supplier?: IUISupplier, triggerLocationParam?: string) =>
  ({ dispatch, getState }: IDeps) => {
    const state = getState();
    const { supplierToBook, triggerLocation = '' } = state.shortlist.confirmBookingModal || {};
    if (supplierToBook || supplier) {
      const bookedSupplier = supplier
        ? { ...supplier, booked: true }
        : { ...supplierToBook, booked: true };

      // Added setTimeout to fix breaking modals when a new one is shown
      setTimeout(() => {
        dispatch(
          saveToShortlist({
            supplier: bookedSupplier as IUISupplier,
            method: 'confirmSupplierPopup',
            shortlistedLocation: (triggerLocationParam ?? triggerLocation) as ShortlistedLocation,
            showSnackbar: false,
          }),
        );
        dispatch(
          bookedSupplierAnalytics({
            supplier: bookedSupplier as IUISupplier,
            method: (triggerLocationParam ?? triggerLocation) as BookedMethod,
          }),
        );
      }, 0);
    }

    return closeBookingConfirmationModal();
  };

export const setFromBookedSupplier = (payload: boolean) => ({
  type: ShortlistActionTypes.SET_FROM_BOOKED_SUPPLIER,
  payload,
});
